import type { CloudCostLine } from "@hasheeai/types";
import { t } from "../../i18n";
import * as s from "../../styles/shared";
import { CostHelp } from "./CloudCostHelp";
import { costMoney } from "./cloud-cost-data";
import { datesInMonth, isDaily, pricedTotal } from "./cloud-cost-model";
export interface CostSlice {
  name: string;
  value: number;
  color: string;
}
export function CostPie({
  title,
  slices,
}: { title: string; slices: CostSlice[] }): React.JSX.Element {
  const positive = slices.filter((slice) => slice.value > 0);
  const total = positive.reduce((sum, slice) => sum + slice.value, 0);
  let offset = 0;
  const gradient = positive
    .map((slice) => {
      const start = offset;
      offset += (slice.value / total) * 100;
      return `${slice.color} ${start}% ${offset}%`;
    })
    .join(",");
  return (
    <section style={s.card}>
      <h3 style={{ marginTop: 0 }}>
        {title}
        {slices.some((slice) => slice.value < 0) && (
          <CostHelp title={title}>
            <p>{t("cost.adjustments")}</p>
          </CostHelp>
        )}
      </h3>
      <PieRing
        title={title}
        positive={positive}
        total={total}
        gradient={gradient}
      />
      {!positive.length && <p>{t("cost.no_slices")}</p>}
      <PieLegend slices={slices} total={total} />
    </section>
  );
}
type DailyChartProps = {
  month: string;
  lines: CloudCostLine[];
  day: string;
  onDay: (day: string) => void;
};
export function DailyCostChart(props: DailyChartProps): React.JSX.Element {
  const { month, lines, day, onDay } = props;
  const dates = datesInMonth(month, new Date().toISOString().slice(0, 10));
  const values = dates.map((date) => {
    const rows = lines.filter(
      (line) =>
        isDaily(line) &&
        line.start.startsWith(date) &&
        line.amount !== null &&
        line.currency.toUpperCase() === "USD",
    );
    return { date, value: rows.length ? pricedTotal(rows) : null };
  });
  const max = Math.max(0.01, ...values.map((row) => row.value ?? 0));
  return (
    <section style={{ ...s.card, margin: "20px 0" }}>
      <h3>
        {t("cost.trend")} · {month}
        <CostHelp title={t("cost.trend")}>
          <p>{t("cost.trend_note")}</p>
          <p>{t("cost.unknown_currency")}</p>
        </CostHelp>
      </h3>
      <div
        style={{
          display: "flex",
          height: 170,
          alignItems: "end",
          gap: 4,
          overflowX: "auto",
        }}
      >
        {values.map((row) => (
          <DayBar key={row.date} row={row} day={day} max={max} onDay={onDay} />
        ))}
      </div>
      <p style={s.cardLabel}>
        {t("cost.date_coverage")}:{" "}
        {values.filter((row) => row.value !== null).length} / {dates.length}
      </p>
      {lines.some((line) => !isDaily(line)) && <p>{t("cost.month_only")}</p>}
    </section>
  );
}

function PieRing({
  title,
  positive,
  total,
  gradient,
}: {
  title: string;
  positive: CostSlice[];
  total: number;
  gradient: string;
}): React.JSX.Element {
  return (
    <div
      role="img"
      aria-label={`${title}: ${positive.map((slice) => `${slice.name} ${costMoney(slice.value)}`).join("; ")}`}
      style={{
        width: 180,
        height: 180,
        borderRadius: "50%",
        margin: "18px auto",
        background:
          total > 0 ? `conic-gradient(${gradient})` : "var(--color-border)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        style={{
          width: 116,
          height: 116,
          borderRadius: "50%",
          background: "var(--color-bg)",
          display: "grid",
          placeContent: "center",
          textAlign: "center",
        }}
      >
        <strong>{total > 0 ? costMoney(total) : "—"}</strong>
        <small>{t("cost.variable_month")}</small>
      </div>
    </div>
  );
}
function PieLegend({
  slices,
  total,
}: { slices: CostSlice[]; total: number }): React.JSX.Element {
  return (
    <>
      {" "}
      {slices.map((slice) => (
        <div
          key={slice.name}
          style={{
            display: "grid",
            gridTemplateColumns: "12px 1fr auto auto",
            gap: 10,
            margin: "10px 0",
            alignItems: "center",
          }}
        >
          <span
            style={{
              background: slice.color,
              width: 10,
              height: 10,
              borderRadius: 3,
            }}
          />
          <span>{slice.name}</span>
          <strong>{costMoney(slice.value)}</strong>
          <span style={{ ...s.cardLabel, minWidth: 48, textAlign: "right" }}>
            {slice.value >= 0 && total > 0
              ? `${((slice.value / total) * 100).toFixed(1)}%`
              : "—"}
          </span>
        </div>
      ))}
    </>
  );
}

function DayBar({
  row,
  day,
  max,
  onDay,
}: {
  row: { date: string; value: number | null };
  day: string;
  max: number;
  onDay: (day: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      key={row.date}
      onClick={() => onDay(row.date)}
      title={`${row.date}: ${row.value === null ? t("cost.no_data") : `${t("cost.partial_daily")} ${costMoney(row.value)}`}`}
      aria-label={`${row.date}: ${row.value === null ? t("cost.no_data") : `${t("cost.partial_daily")} ${costMoney(row.value)}`}`}
      aria-pressed={row.date === day}
      style={dayBarStyle}
    >
      <span
        style={{
          width: "100%",
          minHeight: 3,
          height:
            row.value === null
              ? 4
              : `${Math.max(2, (Math.max(0, row.value) / max) * 130)}px`,
          background:
            row.value === null
              ? "#d5dbe3"
              : row.date === day
                ? "#334f83"
                : "#158579",
          borderRadius: "3px 3px 0 0",
          outline: row.date === day ? "2px solid #334f83" : undefined,
        }}
      />
      <span style={{ fontSize: 11, marginTop: 10 }}>{row.date.slice(8)}</span>
    </button>
  );
}

const dayBarStyle: React.CSSProperties = {
  flex: "1 0 20px",
  height: "100%",
  padding: "0 1px",
  background: "transparent",
  border: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "end",
  cursor: "pointer",
  color: "var(--color-text)",
};
